// Setup instructions rendered inline in the API Keys panel.
// Adding a new secret? Add an entry here too.

export interface SecretGuide {
  title: string;
  intro?: string;
  steps: string[];
  signupUrl?: string;
  docsUrl?: string;
  notes?: string[];
  /** If set, "Save & Test" sends a test email via this provider after save. */
  emailProvider?: "resend" | "sendgrid";
}

export const SECRET_GUIDES: Record<string, SecretGuide> = {
  RESEND_API_KEY: {
    emailProvider: "resend",
    title: "Get a Resend API key",
    intro: "Resend is the recommended outgoing email provider — fastest setup, free tier, no DNS required to start.",
    steps: [
      "Sign up at resend.com (no credit card required).",
      "Verify your email address.",
      "Go to API Keys (resend.com/api-keys) and click 'Create API Key'.",
      "Name it something like 'Helpdesk', set permission to 'Sending access'.",
      "Copy the key — it starts with re_ — and paste it below.",
    ],
    signupUrl: "https://resend.com/signup",
    docsUrl: "https://resend.com/docs/api-reference/emails/send-email",
    notes: [
      "Out of the box you can send from onboarding@resend.dev. To send from your own domain (support@yourcompany.com), add a domain at resend.com/domains and Resend gives you 3 DNS records to paste.",
      "Free tier covers 100 emails/day, 3,000/month.",
    ],
  },

  SENDGRID_API_KEY: {
    emailProvider: "sendgrid",
    title: "Get a SendGrid API key",
    intro: "Use SendGrid if you already have an account or need higher volume than Resend's free tier.",
    steps: [
      "Sign up at sendgrid.com/free.",
      "Verify a sender — Settings → Sender Authentication → Single Sender Verification (or full Domain Authentication).",
      "Settings → API Keys → Create API Key with 'Full Access' or 'Restricted Access' (Mail Send only is enough).",
      "Copy the key — it starts with SG. — and paste it below.",
    ],
    signupUrl: "https://signup.sendgrid.com",
    docsUrl: "https://docs.sendgrid.com/api-reference/mail-send/mail-send",
    notes: [
      "SendGrid REQUIRES sender verification before any emails will deliver. The API will return 403 until you verify.",
      "Free tier: 100 emails/day forever.",
    ],
  },

  GEMINI_API_KEY: {
    title: "Get a Gemini API key",
    steps: [
      "Visit aistudio.google.com/app/apikey and sign in with Google.",
      "Click 'Create API Key' and select a Google Cloud project.",
      "Copy the key — starts with AIza — and paste it below.",
    ],
    signupUrl: "https://aistudio.google.com/app/apikey",
    notes: [
      "Free tier has rate limits; for production use, enable billing on the Cloud project.",
      "Recommended: in Cloud Console, restrict the key to your application's domain.",
    ],
  },

  OPENAI_API_KEY: {
    title: "Get an OpenAI API key",
    steps: [
      "Sign in at platform.openai.com.",
      "Go to API Keys (platform.openai.com/api-keys).",
      "Click 'Create new secret key', name it, copy — starts with sk-.",
      "Make sure your account has billing set up (Settings → Billing).",
    ],
    signupUrl: "https://platform.openai.com/api-keys",
  },

  ANTHROPIC_API_KEY: {
    title: "Get an Anthropic API key",
    steps: [
      "Sign in at console.anthropic.com.",
      "Settings → API Keys → Create Key.",
      "Copy the key — starts with sk-ant- — and paste below.",
      "Add credits at Plans & Billing if your workspace doesn't have them.",
    ],
    signupUrl: "https://console.anthropic.com/account/keys",
  },

  OPENROUTER_API_KEY: {
    title: "Get an OpenRouter API key",
    intro: "OpenRouter is a single API in front of hundreds of models from every major lab. One key and one bill, and you can switch models from the AI & Triage panel without touching another provider account.",
    steps: [
      "Sign up at openrouter.ai (Google or GitHub sign-in works).",
      "Add credit under Settings → Credits — most models are billed per token, and there are free models if you just want to try it.",
      "Go to openrouter.ai/keys and click 'Create Key'.",
      "Name it something like 'Helpdesk', leave the credit limit blank for no cap (or set one to bound spend).",
      "Copy the key — it starts with sk-or-v1- — and paste it below.",
    ],
    signupUrl: "https://openrouter.ai/keys",
    docsUrl: "https://openrouter.ai/docs/api-reference/overview",
    notes: [
      "Pick the model in Settings → AI & Triage. The picker lists every model OpenRouter currently offers with its live per-million-token pricing.",
      "Setting a credit limit on the key is the simplest way to cap what the helpdesk can spend.",
    ],
  },

  GWS_SERVICE_ACCOUNT_KEY: {
    title: "Google Workspace service account JSON",
    intro: "Used by the GWS sync function to read your Workspace directory.",
    steps: [
      "In Google Cloud Console, create a service account in your project.",
      "Grant it the 'Service Account Token Creator' role.",
      "Generate a JSON key, download the file.",
      "In Google Workspace Admin Console → Security → Access and Data Control → API controls → Domain-wide Delegation, add the service account's client ID with these scopes: https://www.googleapis.com/auth/admin.directory.user.readonly, https://www.googleapis.com/auth/admin.directory.group.readonly",
      "Paste the entire JSON file contents below as the secret value.",
    ],
    docsUrl: "https://developers.google.com/admin-sdk/directory/v1/guides/delegation",
  },

  SLACK_BOT_TOKEN: {
    title: "Slack bot OAuth token",
    steps: [
      "Create a Slack app at api.slack.com/apps → 'From scratch'.",
      "Add scopes (OAuth & Permissions): chat:write, channels:read, users:read.",
      "Install the app to your workspace.",
      "Copy the Bot User OAuth Token — starts with xoxb- — and paste below.",
    ],
    signupUrl: "https://api.slack.com/apps",
  },

  M365_CLIENT_SECRET: {
    title: "Microsoft 365 client secret",
    intro: "(Coming soon — config UI only.)",
    steps: [
      "Register an app in Microsoft Entra (Azure AD) admin center.",
      "Generate a client secret in 'Certificates & secrets'.",
      "Grant Microsoft Graph permissions for User.Read.All and Group.Read.All.",
      "Paste the secret value below.",
    ],
  },

  TEAMS_WEBHOOK_URL: {
    title: "Microsoft Teams incoming webhook",
    intro: "(Coming soon — config UI only.)",
    steps: [
      "In Teams, go to the channel where alerts should post.",
      "Click ••• → Connectors → Incoming Webhook → Configure.",
      "Name it, optionally upload an icon, click Create.",
      "Copy the webhook URL and paste below.",
    ],
  },

  INBOUND_EMAIL_SECRET: {
    title: "Inbound email webhook shared secret (optional)",
    intro: "Locks the inbound email webhook so only your email provider can post to it. The secret format depends on which provider you use.",
    steps: [
      "Resend: in the Webhooks section, create a webhook pointing at the inboundEmail URL, then copy the 'Signing Secret' (starts with whsec_) and paste it below. The function verifies Svix-style HMAC signatures on every request.",
      "SendGrid / Postmark: generate any random string of 24+ chars, paste below, then configure your provider to include this header on every webhook POST: x-helpdesk-inbound-secret: <your value>",
    ],
    notes: [
      "Without this set, the inbound webhook accepts any POST that has a known-user 'from' address. With it set, the function detects whether the incoming request has Svix headers (Resend) or expects the literal x-helpdesk-inbound-secret header (SendGrid/Postmark) and verifies accordingly.",
    ],
  },
};
